import { LoaderCircle, MoreHorizontal } from 'lucide-react';
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ComponentRef,
  type ReactNode,
} from 'react';

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  cn,
} from '@shigo-ui/ui';

import {
  dashboardTableActionMenuContentClassName,
  dashboardTableActionMenuItemClassName,
} from './DashboardDataTable.styles';

export interface DashboardTableRowActionsProps {
  /** Accessible name of the trigger, e.g. "Actions for Acme". */
  label: string;
  /** `DashboardTableRowActionItem`s (or other dropdown menu content). */
  children: ReactNode;
  /** Shows a spinner in place of the icon and disables the trigger. */
  pending?: boolean | undefined;
  disabled?: boolean | undefined;
  triggerClassName?: string | undefined;
  contentClassName?: string | undefined;
}

/** The ⋯ menu at the end of a table row. */
export function DashboardTableRowActions({
  label,
  children,
  pending = false,
  disabled = false,
  triggerClassName,
  contentClassName,
}: DashboardTableRowActionsProps) {
  return (
    // Not modal: items open dialogs, which must not fight the menu's focus trap.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={triggerClassName}
          disabled={disabled || pending}
          aria-label={label}
        >
          {pending ? (
            <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={dashboardTableActionMenuContentClassName(contentClassName)}
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const DashboardTableRowActionItem = forwardRef<
  ComponentRef<typeof DropdownMenuItem>,
  ComponentPropsWithoutRef<typeof DropdownMenuItem>
>(({ className, ...props }, ref) => (
  <DropdownMenuItem
    ref={ref}
    className={cn(dashboardTableActionMenuItemClassName(), className)}
    {...props}
  />
));
DashboardTableRowActionItem.displayName = 'DashboardTableRowActionItem';
